/**
 * React Query hooks for volume operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useVolumes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { volumeService } from '@/services';

/**
 * Query key factory for volumes.
 * Ensures consistent cache key generation.
 */
export const volumeKeys = {
  all: ['volumes'] as const,
  lists: () => [...volumeKeys.all, 'list'] as const,
  list: () => [...volumeKeys.lists()] as const,
  details: () => [...volumeKeys.all, 'detail'] as const,
  detail: (name: string) => [...volumeKeys.details(), name] as const,
};

/**
 * Hook to fetch volumes with pagination
 *
 * @param page - Page number (1-based)
 * @param pageSize - Number of items per page
 * @example
 * const { data, isLoading, error } = useVolumes(1, 25);
 */
export function useVolumes(page: number = 1, pageSize: number = 25, enabled: boolean = true) {
  return useQuery({
    queryKey: [...volumeKeys.list(), page, pageSize],
    queryFn: () => volumeService.list(page, pageSize),
    enabled,
    staleTime: 0, // Always refetch on mount so attachment state is current
  });
}

/**
 * Hook to fetch a single volume by name
 *
 * @param name - The volume name
 * @example
 * const { data, isLoading, error } = useVolume('my-volume');
 */
export function useVolume(name: string, enabled: boolean = true) {
  return useQuery({
    queryKey: volumeKeys.detail(name),
    queryFn: () => volumeService.getByName(name),
    enabled: !!name && enabled,
    staleTime: 0, // Always refetch on mount so attachment state is current
  });
}

/**
 * Hook to create a volume
 *
 * @example
 * const createMutation = useCreateVolume();
 * createMutation.mutate({ name: 'my-volume', type: 'AzureBlob' });
 */
export function useCreateVolume() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      type,
      size,
      labels,
    }: {
      name: string;
      type: string;
      size?: string;
      labels?: Record<string, string>;
    }) => volumeService.create(name, { type, size, labels }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: volumeKeys.all });
    },
  });
}

/**
 * Hook to delete a volume
 *
 * @example
 * const deleteMutation = useDeleteVolume();
 * deleteMutation.mutate('volume-name');
 */
export function useDeleteVolume() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => volumeService.delete(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: volumeKeys.all });
    },
  });
}

/**
 * Hook to fork a data disk volume
 *
 * @example
 * const forkMutation = useForkVolume();
 * forkMutation.mutate({ sourceVolumeName: 'src', destinationVolumeName: 'dst' });
 */
export function useForkVolume() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceVolumeName,
      destinationVolumeName,
      labels,
    }: {
      sourceVolumeName: string;
      destinationVolumeName: string;
      labels?: Record<string, string>;
    }) => volumeService.fork(sourceVolumeName, { destinationVolumeName, labels }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: volumeKeys.all });
    },
  });
}
